import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { CartItem } from "@/types";
import { cartKey } from "@/lib/utils";

interface CartStore {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, variantId: string | undefined, quantity: number) => void;
  clearCart: () => void;
  itemCount: () => number;
  subtotal: () => number;
  dealerSubtotal: (discountPercentage: number) => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => {
        const k = cartKey(item.productId, item.variantId);
        set((state) => {
          const existing = state.items.find((i) => cartKey(i.productId, i.variantId) === k);
          const maxQty = item.trackStock && item.stock !== undefined ? item.stock : Infinity;
          if (existing) {
            const newQty = Math.min(existing.quantity + item.quantity, maxQty);
            if (newQty === existing.quantity) return state; // stok dolu, değiştirme
            return {
              items: state.items.map((i) =>
                cartKey(i.productId, i.variantId) === k
                  ? { ...i, quantity: newQty, stock: item.stock, trackStock: item.trackStock }
                  : i
              ),
            };
          }
          const clampedQty = Math.min(item.quantity, maxQty);
          return { items: [...state.items, { ...item, quantity: clampedQty }] };
        });
      },

      removeItem: (productId, variantId) => {
        const k = cartKey(productId, variantId);
        set((state) => ({
          items: state.items.filter((i) => cartKey(i.productId, i.variantId) !== k),
        }));
      },

      updateQuantity: (productId, variantId, quantity) => {
        const k = cartKey(productId, variantId);
        if (quantity <= 0) {
          set((state) => ({
            items: state.items.filter((i) => cartKey(i.productId, i.variantId) !== k),
          }));
          return;
        }
        set((state) => {
          const item = state.items.find((i) => cartKey(i.productId, i.variantId) === k);
          // Stok takibi açıksa stok sınırını aşma
          const maxQty = item?.trackStock && item.stock !== undefined ? item.stock : Infinity;
          const clampedQty = Math.min(quantity, maxQty);
          return {
            items: state.items.map((i) =>
              cartKey(i.productId, i.variantId) === k ? { ...i, quantity: clampedQty } : i
            ),
          };
        });
      },

      clearCart: () => set({ items: [] }),
      itemCount: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
      subtotal: () => get().items.reduce((sum, i) => sum + i.price * i.quantity, 0),
      dealerSubtotal: (discountPercentage: number) =>
        get().items.reduce((sum, i) => {
          const unitPrice =
            i.dealerPrice ??
            (discountPercentage > 0 ? i.price * (1 - discountPercentage / 100) : i.price);
          return sum + unitPrice * i.quantity;
        }, 0),
    }),
    { name: "hba-cart" }
  )
);
